import React, { useState, useEffect, useCallback } from 'react';

/**
 * SRE V2 Slab Cleaning Debug Tool
 * 
 * This is a DEBUG TOOL for testing the slab cleaning pipeline.
 * NOT a production UI.
 * 
 * Features:
 * - Upload image → R2 → images table
 * - Create slab with uploaded image
 * - Trigger cleaning endpoint
 * - Display original vs cleaned side-by-side
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Simple fetch wrapper
const api = {
  async get(endpoint) {
    const response = await fetch(`${API_URL}${endpoint}`);
    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || `HTTP ${response.status}`);
    }
    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  },
  async post(endpoint, data) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || `HTTP ${response.status}`);
    }
    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  },
  async postForm(endpoint, formData) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      body: formData
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || `HTTP ${response.status}`);
    }
    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  }
};

export default function SlabCleaningDebugPage() {
  // State
  const [slabs, setSlabs] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedSlab, setSelectedSlab] = useState(null);
  const [cleaningResult, setCleaningResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  
  // Form state for new slab
  const [newSlab, setNewSlab] = useState({
    commercial_name: '',
    supplier_name: '',
    origin_country: 'Italia',
    width_cm: 300,
    height_cm: 200,
    thickness_cm: 2
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [slabsData, imagesData] = await Promise.all([
        api.get('/api/sre-v2/slabs?limit=50'),
        api.get('/api/sre-v2/images?limit=50')
      ]);
      setSlabs(slabsData.slabs || []);
      setImages(imagesData.images || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Upload image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadProgress('Uploading...');
      setError(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'slab_raw');

      const result = await api.postForm('/api/sre-v2/images/upload', formData);
      
      setUploadProgress(null);
      setImages(prev => [result.image, ...prev]);
      
      // Auto-fill slab form with image ID
      if (result.image?.id) {
        setNewSlab(prev => ({ ...prev, primary_image_id: result.image.id }));
      }
    } catch (err) {
      setError(err.message);
      setUploadProgress(null);
    }
  };

  // Create slab
  const handleCreateSlab = async (e) => {
    e.preventDefault();
    
    if (!newSlab.primary_image_id) {
      setError('Please upload an image first');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const slabData = {
        commercial_name: newSlab.commercial_name,
        supplier_name: newSlab.supplier_name,
        origin_country: newSlab.origin_country,
        dimensions: {
          width_cm: parseFloat(newSlab.width_cm),
          height_cm: parseFloat(newSlab.height_cm),
          thickness_cm: parseFloat(newSlab.thickness_cm)
        },
        primary_image_id: newSlab.primary_image_id
      };

      const result = await api.post('/api/sre-v2/slabs', slabData);
      
      setSlabs(prev => [result.slab, ...prev]);
      setSelectedSlab(result.slab);
      
      // Reset form
      setNewSlab({
        commercial_name: '',
        supplier_name: '',
        origin_country: 'Italia',
        width_cm: 300,
        height_cm: 200,
        thickness_cm: 2
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Trigger cleaning
  const handleCleanSlab = async (slab) => {
    try {
      setLoading(true);
      setError(null);
      setCleaningResult(null);

      const result = await api.post(`/api/sre-v2/slabs/${slab.id}/clean`, {});
      
      setCleaningResult(result);
      
      // Reload data to get updated slab
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get image URL helper
  const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === 'string') return image;
    return image.url || null;
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ borderBottom: '2px solid #333', paddingBottom: '10px' }}>
        🔧 SRE V2 Slab Cleaning - DEBUG TOOL
      </h1>
      
      {error && (
        <div style={{ 
          background: '#fee', 
          border: '1px solid #c00', 
          padding: '10px', 
          marginBottom: '20px',
          whiteSpace: 'pre-wrap'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        
        {/* LEFT PANEL: Upload & Create */}
        <div>
          <h2>📤 Step 1: Upload Image</h2>
          <div style={{ border: '1px solid #ccc', padding: '15px', marginBottom: '20px' }}>
            <input 
              type="file" 
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              disabled={loading}
            />
            {uploadProgress && <span style={{ marginLeft: '10px' }}>{uploadProgress}</span>}
            
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              Upload a raw slab photo (JPEG, PNG, WebP)
            </div>
          </div>

          <h2>📝 Step 2: Create Slab</h2>
          <form onSubmit={handleCreateSlab} style={{ border: '1px solid #ccc', padding: '15px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Commercial Name:</label>
              <input 
                type="text"
                value={newSlab.commercial_name}
                onChange={e => setNewSlab(prev => ({ ...prev, commercial_name: e.target.value }))}
                required
                style={{ width: '100%', padding: '5px' }}
              />
            </div>
            
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Supplier:</label>
              <input 
                type="text"
                value={newSlab.supplier_name}
                onChange={e => setNewSlab(prev => ({ ...prev, supplier_name: e.target.value }))}
                required
                style={{ width: '100%', padding: '5px' }}
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Width (cm):</label>
                <input 
                  type="number"
                  value={newSlab.width_cm}
                  onChange={e => setNewSlab(prev => ({ ...prev, width_cm: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '5px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Height (cm):</label>
                <input 
                  type="number"
                  value={newSlab.height_cm}
                  onChange={e => setNewSlab(prev => ({ ...prev, height_cm: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '5px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Thickness (cm):</label>
                <input 
                  type="number"
                  step="0.1"
                  value={newSlab.thickness_cm}
                  onChange={e => setNewSlab(prev => ({ ...prev, thickness_cm: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '5px' }}
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Primary Image ID:</label>
              <select
                value={newSlab.primary_image_id || ''}
                onChange={e => setNewSlab(prev => ({ ...prev, primary_image_id: e.target.value }))}
                required
                style={{ width: '100%', padding: '5px' }}
              >
                <option value="">-- Select uploaded image --</option>
                {images.filter(img => img.image_type === 'slab_raw').map(img => (
                  <option key={img.id} value={img.id}>
                    {img.id.slice(0, 8)}... ({img.width_px}x{img.height_px})
                  </option>
                ))}
              </select>
            </div>
            
            <button 
              type="submit" 
              disabled={loading || !newSlab.primary_image_id}
              style={{ 
                padding: '10px 20px', 
                background: '#007bff', 
                color: '#fff', 
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating...' : 'Create Slab'}
            </button>
          </form>

          <h2>📋 Existing Slabs</h2>
          <div style={{ border: '1px solid #ccc', padding: '15px', maxHeight: '300px', overflow: 'auto' }}>
            {slabs.length === 0 ? (
              <p style={{ color: '#666' }}>No slabs yet. Create one above.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ padding: '5px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '5px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '5px', textAlign: 'left' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {slabs.map(slab => (
                    <tr key={slab.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '5px' }}>{slab.commercial_name}</td>
                      <td style={{ padding: '5px' }}>
                        <span style={{ 
                          padding: '2px 6px', 
                          borderRadius: '3px',
                          background: slab.cleaning_status === 'completed' ? '#afa' : 
                                     slab.cleaning_status === 'failed' ? '#faa' :
                                     slab.cleaning_status === 'manual_required' ? '#ffa' : '#eee',
                          fontSize: '11px'
                        }}>
                          {slab.cleaning_status || 'pending'}
                        </span>
                      </td>
                      <td style={{ padding: '5px' }}>
                        <button
                          onClick={() => {
                            setSelectedSlab(slab);
                            setCleaningResult(null);
                          }}
                          style={{ marginRight: '5px', padding: '3px 8px', fontSize: '11px' }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleCleanSlab(slab)}
                          disabled={loading}
                          style={{ 
                            padding: '3px 8px', 
                            fontSize: '11px',
                            background: '#28a745',
                            color: '#fff',
                            border: 'none'
                          }}
                        >
                          Clean
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Preview & Results */}
        <div>
          <h2>🖼️ Step 3: View & Clean</h2>
          
          {selectedSlab ? (
            <div style={{ border: '1px solid #ccc', padding: '15px' }}>
              <h3 style={{ margin: '0 0 10px 0' }}>{selectedSlab.commercial_name}</h3>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 15px 0' }}>
                ID: {selectedSlab.id}<br/>
                Dimensions: {selectedSlab.width_cm}x{selectedSlab.height_cm}x{selectedSlab.thickness_cm} cm<br/>
                Supplier: {selectedSlab.supplier_name}<br/>
                Status: {selectedSlab.cleaning_status || 'pending'}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                {/* Original Image */}
                <div>
                  <h4 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Original Image</h4>
                  {images.find(img => img.id === selectedSlab.primary_image_id)?.url ? (
                    <img 
                      src={images.find(img => img.id === selectedSlab.primary_image_id)?.url}
                      alt="Original"
                      style={{ width: '100%', border: '1px solid #ccc' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{ background: '#f0f0f0', padding: '40px', textAlign: 'center', color: '#666' }}>
                      No image available
                    </div>
                  )}
                </div>

                {/* Cleaned Image */}
                <div>
                  <h4 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Cleaned Image</h4>
                  {cleaningResult?.cleaned_image_url ? (
                    <img 
                      src={cleaningResult.cleaned_image_url}
                      alt="Cleaned"
                      style={{ width: '100%', border: '1px solid #0a0' }}
                    />
                  ) : selectedSlab.cleaned_image_id ? (
                    <div style={{ background: '#afa', padding: '40px', textAlign: 'center' }}>
                      Cleaned image exists (ID: {selectedSlab.cleaned_image_id.slice(0, 8)}...)
                    </div>
                  ) : (
                    <div style={{ background: '#f0f0f0', padding: '40px', textAlign: 'center', color: '#666' }}>
                      Not cleaned yet. Click "Clean" button.
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={() => handleCleanSlab(selectedSlab)}
                disabled={loading}
                style={{ 
                  padding: '10px 20px', 
                  background: '#28a745', 
                  color: '#fff', 
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {loading ? 'Processing...' : '🧹 Run Cleaning Pipeline'}
              </button>

              {/* Cleaning Result */}
              {cleaningResult && (
                <div style={{ 
                  marginTop: '15px', 
                  padding: '10px', 
                  background: cleaningResult.status === 'completed' ? '#afa' : 
                             cleaningResult.status === 'manual_required' ? '#ffa' : '#faa',
                  borderRadius: '5px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>Cleaning Result</h4>
                  <pre style={{ margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(cleaningResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div style={{ border: '1px solid #ccc', padding: '40px', textAlign: 'center', color: '#666' }}>
              Select a slab from the list to view and clean
            </div>
          )}

          <h2 style={{ marginTop: '20px' }}>📷 Uploaded Images</h2>
          <div style={{ border: '1px solid #ccc', padding: '15px', maxHeight: '300px', overflow: 'auto' }}>
            {images.length === 0 ? (
              <p style={{ color: '#666' }}>No images uploaded yet.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {images.map(img => (
                  <div key={img.id} style={{ border: '1px solid #eee', padding: '5px' }}>
                    {img.url && img.url !== '/api/static/placeholder.svg' ? (
                      <img 
                        src={img.url} 
                        alt={img.image_type}
                        style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                        onError={(e) => { e.target.src = '/api/static/placeholder.svg'; }}
                      />
                    ) : (
                      <div style={{ width: '100%', height: '80px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>
                        Preview Mode
                      </div>
                    )}
                    <div style={{ fontSize: '10px', marginTop: '5px' }}>
                      <div>{img.id.slice(0, 8)}...</div>
                      <div>{img.image_type}</div>
                      <div>{img.width_px}x{img.height_px}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: '#fff', padding: '20px 40px', borderRadius: '5px' }}>
            Processing...
          </div>
        </div>
      )}
    </div>
  );
}
